import { useEffect, useState } from "react";
import { api, formatPrice, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Euro, Receipt, ShoppingBag, Users, Wallet, RotateCcw, UserX, FileSpreadsheet, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";

export function Analytics() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCustomersOpen, setConfirmCustomersOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [exportMonth, setExportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.get("/stats/export", { params: { month: exportMonth }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `incassi-${exportMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("export_done"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const load = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = async () => {
    setResetting(true);
    try {
      await api.post("/stats/reset");
      toast.success(t("stats_reset"));
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResetting(false);
    }
  };

  const resetCustomers = async () => {
    setResetting(true);
    try {
      await api.post("/customers/reset");
      toast.success(t("customers_reset"));
      setConfirmCustomersOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResetting(false);
    }
  };

  if (!stats) return <p className="text-sm text-[#786C66]">{t("loading")}</p>;

  const kpis = [
    { icon: Euro, label: t("revenue_total"), value: formatPrice(stats.revenue_total), testid: "kpi-revenue-total" },
    { icon: Wallet, label: t("to_collect"), value: formatPrice(stats.revenue_unpaid), testid: "kpi-to-collect" },
    { icon: Receipt, label: t("orders_today"), value: stats.orders_today, testid: "kpi-orders-today" },
    { icon: ShoppingBag, label: t("avg_order"), value: formatPrice(stats.avg_order), testid: "kpi-avg-order" },
    { icon: Users, label: t("customers_total"), value: stats.customers_total, testid: "kpi-customers" },
  ];

  return (
    <div data-testid="analytics-dashboard">
      <div className="flex justify-end gap-2 mb-4 flex-wrap">
        <Button
          data-testid="reset-customers-btn"
          variant="outline"
          size="sm"
          onClick={() => setConfirmCustomersOpen(true)}
          className="rounded-full border-red-200 text-red-600 hover:bg-red-50 gap-1.5"
        >
          <UserX className="w-4 h-4" /> {t("reset_customers")}
        </Button>
        <Button
          data-testid="reset-stats-btn"
          variant="outline"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          className="rounded-full border-red-200 text-red-600 hover:bg-red-50 gap-1.5"
        >
          <RotateCcw className="w-4 h-4" /> {t("reset_stats")}
        </Button>
      </div>
      <Dialog open={confirmCustomersOpen} onOpenChange={setConfirmCustomersOpen}>
        <DialogContent className="bg-[#FDFBF7] max-w-sm" data-testid="reset-customers-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#4A2C2A]">{t("reset_customers_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#786C66]">{t("reset_customers_desc")}</p>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="confirm-reset-customers-btn"
              onClick={resetCustomers}
              disabled={resetting}
              className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {t("confirm")}
            </Button>
            <Button variant="outline" onClick={() => setConfirmCustomersOpen(false)} className="rounded-full border-[#E8DFD5]">
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#FDFBF7] max-w-sm" data-testid="reset-stats-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#4A2C2A]">{t("reset_confirm_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#786C66]">{t("reset_confirm_desc")}</p>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="confirm-reset-stats-btn"
              onClick={reset}
              disabled={resetting}
              className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {t("confirm")}
            </Button>
            <Button data-testid="cancel-reset-stats-btn" variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-full border-[#E8DFD5]">
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k) => (
          <div key={k.testid} data-testid={k.testid} className="bg-white border border-[#E8DFD5] rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
            <div className="flex items-center gap-2 text-[#786C66] text-xs uppercase tracking-widest font-semibold">
              <k.icon className="w-4 h-4 text-[#D97706]" /> {k.label}
            </div>
            <p className="font-heading text-3xl font-bold text-[#1F1615] mt-2">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E8DFD5] rounded-2xl p-5 mb-6" data-testid="revenue-history">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-heading font-semibold text-[#4A2C2A]">{t("revenue_30d")}</h3>
          <span className="text-sm font-bold text-[#D97706]" data-testid="revenue-30d-total">{formatPrice(stats.revenue_30d_total)}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-4 bg-[#F6F1E9] border border-[#E8DFD5] rounded-xl p-3">
          <FileSpreadsheet className="w-4 h-4 text-emerald-700" />
          <span className="text-xs font-semibold text-[#4A2C2A]">{t("export_excel")}</span>
          <input data-testid="export-month-input" type="month" value={exportMonth} onChange={(e) => setExportMonth(e.target.value)} className="h-8 rounded-md border border-[#E8DFD5] bg-white px-2 text-sm" />
          <Button data-testid="export-excel-btn" size="sm" disabled={exporting || !exportMonth} onClick={exportExcel} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5 h-8">
            <Download className="w-3.5 h-3.5" /> {t("download")}
          </Button>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={stats.revenue_30d}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8DFD5" />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#786C66" }} interval={window.innerWidth < 640 ? 4 : 1} />
            <YAxis tick={{ fontSize: 11, fill: "#786C66" }} tickFormatter={(v) => `€${v}`} width={48} />
            <Tooltip formatter={(v, name) => [formatPrice(v), name === "paid" ? t("pay_paid") : t("revenue_total")]} />
            <Bar dataKey="revenue" fill="#D97706" radius={[4, 4, 0, 0]} />
            <Bar dataKey="paid" fill="#4A2C2A" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex gap-4 text-xs text-[#786C66] mt-2">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#D97706] inline-block" /> {t("revenue_total")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#4A2C2A] inline-block" /> {t("pay_paid")}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E8DFD5] rounded-2xl p-5">
          <h3 className="font-heading font-semibold text-[#4A2C2A] mb-4">{t("orders_by_day")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats.by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8DFD5" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#786C66" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#786C66" }} />
              <Tooltip />
              <Area type="monotone" dataKey="orders" stroke="#D97706" fill="#D97706" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E8DFD5] rounded-2xl p-5">
          <h3 className="font-heading font-semibold text-[#4A2C2A] mb-4">{t("orders_by_hour")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.by_hour}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8DFD5" />
              <XAxis dataKey="hour" tick={{ fontSize: 12, fill: "#786C66" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#786C66" }} />
              <Tooltip />
              <Bar dataKey="orders" fill="#4A2C2A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-[#E8DFD5] rounded-2xl p-5 mt-6">
        <h3 className="font-heading font-semibold text-[#4A2C2A] mb-4">{t("top_items")}</h3>
        <div className="space-y-2">
          {stats.top_items.length === 0 && <p className="text-sm text-[#786C66]">—</p>}
          {stats.top_items.map((item, i) => (
            <div key={i} data-testid={`top-item-${i}`} className="flex items-center justify-between border-b border-[#F6F1E9] last:border-0 pb-2">
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-[#F6F1E9] flex items-center justify-center text-xs font-bold text-[#4A2C2A]">{i + 1}</span>
                <span className="font-semibold text-sm text-[#1F1615]">{item.name}</span>
              </div>
              <span className="text-sm text-[#786C66]">{item.qty} {t("sold")} · {formatPrice(item.revenue)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
